import { computed, effect, signal } from "@preact/signals";
import * as _ from "underscore";

import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import type * as spacedRepetition from "shared/spacedRepetition";
import * as unicodePatterns from "shared/unicodePatterns";
import * as utils from "shared/utils";

export interface UserWordData {
  _id?: string;
  word?: string;
  translation?: string;
  alternativeTranslations?: string[];
  alternativeWords?: string[];
  contexts?: any[];
  language?: string;
  interval?: string | number;
  nextDate?: string;
  recalledThisSession?: boolean;
  [key: string]: any;
}

const defaultUserWordData = (): UserWordData => ({
  alternativeTranslations: [],
  alternativeWords: [],
  contexts: [],
});

export default class UserWordStore {
  readonly state = signal<UserWordData>(defaultUserWordData());
  readonly deletedSequence = signal(0);
  readonly viewJSON = computed(() => ({ ...this.state.value }));

  private saveQueue: Promise<any> = Promise.resolve();

  constructor(attributes: UserWordData = {}) {
    this.state.value = { ...defaultUserWordData(), ...attributes };
  }

  get id() {
    return this.state.value._id;
  }

  hasField(key: string) {
    return (
      this.state.value[key] !== undefined && this.state.value[key] !== null
    );
  }

  patch(updates: Partial<UserWordData>) {
    this.state.value = { ...this.state.value, ...updates };
    return this;
  }

  onChange(
    selector: (userWord: UserWordData) => any,
    callback: (value: any, previousValue: any) => void
  ) {
    let previousValue = selector(this.state.value);
    return effect(() => {
      const value = selector(this.state.value);
      if (!_.isEqual(value, previousValue)) {
        const oldValue = previousValue;
        previousValue = value;
        callback(value, oldValue);
      }
    });
  }

  async fetch(options: { success?: (word: UserWordStore) => void } = {}) {
    if (!this.id) {
      throw new Error("Cannot fetch user word without _id");
    }
    const { data } = await readlangClient.request<UserWordData>(
      "GET",
      "/api/userWord/" + this.id,
      {}
    );
    this.patch(data);
    options.success?.(this);
    return this;
  }

  /** Stars or unstars the word, and saves it */
  toggleFavorite() {
    const favorite = !this.state.value.favorite;
    this.patch({ favorite });
    return this.save({ favorite }, { patch: true });
  }

  // Serialize saves per word instance to avoid concurrent PATCH requests on the same word.
  // options.recall describes the exercise behind a spaced repetition update,
  // for the server's Recall record. It is sent but not stored on the word.
  save(updates: Partial<UserWordData> = {}, options: any = {}) {
    const saveTask = async () => {
      if (this.id) {
        await readlangClient.request("PATCH", "/api/userWord/" + this.id, {
          ...updates,
          ...(options.recall ? { recall: options.recall } : {}),
        });
        this.patch(updates);
        options.success?.(this);
        return this;
      }

      const payload = { ...this.state.value, ...updates };
      const { data } = await readlangClient.request<any>(
        "POST",
        "/api/userWord",
        payload
      );

      if (!data.success) {
        console.error("create userWord failed");
        return this;
      }

      this.patch({
        ...updates,
        _id: data.updated || data.upserted,
        ...(data.frequency ? { frequency: data.frequency } : {}),
      });
      options.success?.(this, data);
      return this;
    };

    const savePromise = this.saveQueue.then(saveTask, saveTask);
    this.saveQueue = savePromise.catch(() => undefined);
    return savePromise;
  }

  async deleteUserWord(options: { success?: () => void } = {}) {
    if (this.id) {
      await readlangClient.request("DELETE", "/api/userWord/" + this.id, {});
    }
    this.deletedSequence.value += 1;
    options.success?.();
  }

  /**
   * Undoes a lookup by removing the context it saved, deleting the word only if
   * that was its last context. A lookup of an already-saved word merges into
   * it, so its earlier contexts must survive.
   */
  async removeContext(contextID: string) {
    const { data } = await readlangClient.request<{ deleted: boolean }>(
      "DELETE",
      `/api/userWord/${this.id}/contexts/${contextID}`,
      {}
    );
    if (data.deleted) {
      this.deletedSequence.value += 1;
    } else {
      this.patch({
        contexts: (this.state.value.contexts ?? []).filter(
          (context) => context._id !== contextID
        ),
      });
    }
  }

  lastSeen() {
    if (this.hasField("nextDate")) {
      return new Date(
        new Date(dateUtils.parse(this.state.value.nextDate)).getTime() -
          parseInt(String(this.state.value.interval), 10)
      );
    }
    return null;
  }

  isPhrase() {
    return utils.notWordRegExp().test(this.state.value.word);
  }

  async recall(options: {
    recallEase: number;
    difficulty: number;
    source: spacedRepetition.RecallSource;
  }) {
    await readlangClient.request(
      "POST",
      "/api/userWord/" + this.id + "/recall",
      options
    );
    this.patch({ recalledThisSession: true });
  }

  formatTranslations() {
    return [
      this.state.value.translation,
      ...(this.state.value.alternativeTranslations ?? []),
    ].join(" / ");
  }
}

// [^${letter}] rather than \P{L}, which Kindle's browser doesn't support in
// string-built regexes (unicodePatterns ranges are pre-expanded by Babel).
const wordInContextRegExp = (word: string) => {
  const notLetter = `[^${unicodePatterns.letter}]`;
  return new RegExp(
    `(${notLetter}|^)(${utils.regexpEscape(word)})(${notLetter}|$)`,
    "iu"
  );
};

export const formattedContextHtml = (contextText: string, word: string) => {
  return contextText.replace(
    wordInContextRegExp(word),
    "$1<strong>$2</strong>$3"
  );
};

export type FormattedContextSegment =
  | { type: "text"; value: string }
  | { type: "strong"; value: string };

/**
 * Splits a context string into plain-text and bolded-match segments, suitable
 * for rendering as JSX. The match logic matches `formattedContextHtml`.
 */
export const formatContextSegments = (
  contextText: string,
  word: string
): FormattedContextSegment[] => {
  const match = wordInContextRegExp(word).exec(contextText);

  if (!match) {
    return [{ type: "text", value: contextText }];
  }

  const matchStart = match.index + match[1].length;
  const matchEnd = matchStart + match[2].length;

  const segments: FormattedContextSegment[] = [];
  const before = contextText.substring(0, matchStart);
  if (before) segments.push({ type: "text", value: before });
  segments.push({
    type: "strong",
    value: contextText.substring(matchStart, matchEnd),
  });
  const after = contextText.substring(matchEnd);
  if (after) segments.push({ type: "text", value: after });
  return segments;
};
